import { useEffect, useState } from 'react';

/**
 * Match a media query from JavaScript.
 *
 * Layout belongs in CSS, and nearly all of the responsive behaviour here is
 * done with Tailwind's breakpoints. This is for the cases CSS cannot express:
 * deciding not to *render* something at all rather than hiding it — a
 * spreadsheet grid that would mount, measure, and fetch on a phone before
 * being covered by a message is a waste of the phone's battery and the
 * client's data.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const list = window.matchMedia(query);
    const sync = () => setMatches(list.matches);

    // Re-read on mount: the query may have changed between the initial state
    // and this effect, and on a phone that is a rotation away.
    sync();

    list.addEventListener('change', sync);
    // Belt and braces. `change` is the right event and fires reliably in a
    // visible tab, but it is throttled while a document is hidden — a
    // background tab resized before being brought forward can come back
    // holding a stale answer, and here that answer decides whether the
    // spreadsheet editor renders at all. React bails out when the value has
    // not actually changed, so the extra listener costs nothing.
    window.addEventListener('resize', sync);

    return () => {
      list.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, [query]);

  return matches;
}

/**
 * Below Tailwind's `md`, which is where the sidebar becomes a drawer and the
 * Drive listing drops its metadata columns. Kept as one constant so the CSS
 * breakpoint and the JavaScript one cannot drift apart.
 */
export function useIsSmallScreen(): boolean {
  return useMediaQuery('(max-width: 767px)');
}
